import { useCallback, useEffect } from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { routes } from "config/routes";
import { AddToDroppingListConfirmationModal } from "flows/Inbound/components/AddToDroppingListConfirmationModal";
import { ClaimedlistNotFoundWarningModal } from "flows/Inbound/components/ClaimedlistNotFoundWarningModal";
import { DeleteFinalItemConfirmationModal } from "flows/Inbound/components/DeleteFinalItemConfirmationModal";
import { InboundDroppingList } from "flows/Inbound/components/InboundDroppingList";
import { InboundDroppingListHeader } from "flows/Inbound/components/InboundDroppingListHeader";
import { NewProductsModalModal } from "flows/Inbound/components/NewProductsModal";
import { ProductAlreadyInboundedModal } from "flows/Inbound/components/ProductAlreadyInboundedModal";
import { RefetchClaimedListConfirmationModal } from "flows/Inbound/components/RefetchClaimedListConfirmationModal";
import { ScanningFailedModal } from "flows/Inbound/components/ScanningFailedModal";
import { UnclaimedListConfirmationModal } from "flows/Inbound/components/UnclaimedListConfirmationModal";
import { UnclaimListConfirmationModal } from "flows/Inbound/components/UnclaimListConfirmationModal";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { buildProductPayload } from "flows/Inbound/stores/inboundStore/utils";
import {
  useAddItem,
  useDeleteClaimedProduct,
  useFetchClaimedProducts,
  useUnClaimInboundList,
} from "flows/Inbound/stores/useInboundDropService";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { ClipIcon } from "ui/Icons/Icons";
import { BodyM } from "ui/Typography/Typography";

export function InboundDroppingListPage() {
  const intl = useIntl();
  const navigate = useNavigate();

  const { isFetchingClaimedProducts, isFetchingClaimedProductsSubset } = useFetchClaimedProducts();
  const { unClaimInboundList, isUnClaimingList } = useUnClaimInboundList();
  const { deleteClaimedProduct } = useDeleteClaimedProduct();

  const {
    droppingListId,
    deliverySSCC,
    deliveryType,
    creatorId,
    orderId,
    inboundUIState,
    setInboundUIState,
    droppingListType,
    despatchAdviceItems,
    selectedProduct,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      deliverySSCC: state.deliverySSCC,
      deliveryType: state.deliveryType,
      creatorId: state.creatorId,
      orderId: state.orderId,
      inboundUIState: state.inboundUIState,
      setInboundUIState: state.setInboundUIState,
      droppingListType: state.droppingListType,
      despatchAdviceItems: state.despatchAdviceItems,
      selectedProduct: state.selectedProduct,
    })),
  );

  const {
    isUnclaimListConfirmationModalVisible,
    isDeleteFinalItemModalVisibleForSku,
    isUnclaimedListConfirmationModalVisible,
    isRefetchClaimedListConfirmationModalVisible,
    isClaimedListNotFoundWarningModalVisible,
    isProductAlreadyInbounded,
    isScanningFailedModalVisible,
    isProductNotInDroppingList,
    isNewProductsModalVisible,
  } = inboundUIState;

  useEffect(() => {
    if (!droppingListId) navigate(routes.inbound.root);
  }, [droppingListId, navigate]);

  const handleClose = useCallback(() => {
    setInboundUIState({
      isProductNotInDroppingList: false,
      isProductAlreadyInbounded: false,
      isScanningFailedModalVisible: false,
    });
  }, [setInboundUIState]);
  const { addItemToDroppingList, isAddingProduct } = useAddItem();

  const handleAddProductToDroppingList = useCallback(async () => {
    handleClose();
    if (selectedProduct && droppingListId) {
      const product = buildProductPayload(selectedProduct, despatchAdviceItems, deliverySSCC);
      await addItemToDroppingList({
        listId: droppingListId,
        product,
      });
    }
  }, [
    addItemToDroppingList,
    deliverySSCC,
    despatchAdviceItems,
    droppingListId,
    handleClose,
    selectedProduct,
  ]);

  return (
    <Page isFull isBgGrey height="100%">
      <InboundDroppingListHeader />
      <SpinnerModal
        isOpen={
          (isFetchingClaimedProducts || isUnClaimingList || isAddingProduct) &&
          !isFetchingClaimedProductsSubset
        }
      />
      <InboundDroppingList />
      <UnclaimListConfirmationModal
        isOpen={isUnclaimListConfirmationModalVisible}
        onClickYes={() => {
          if (droppingListId)
            unClaimInboundList({
              listId: droppingListId,
              deliverySSCC,
              deliveryType,
              creatorId,
              orderId,
              droppingListType,
            });
        }}
        onClickNo={() => setInboundUIState({ isUnclaimListConfirmationModalVisible: false })}
      />
      <DeleteFinalItemConfirmationModal
        isOpen={!!isDeleteFinalItemModalVisibleForSku}
        onClickYes={() => {
          if (droppingListId && isDeleteFinalItemModalVisibleForSku)
            deleteClaimedProduct(droppingListId, isDeleteFinalItemModalVisibleForSku);
          navigate(routes.inbound.root);
        }}
        onClickNo={() => setInboundUIState({ isDeleteFinalItemModalVisibleForSku: null })}
      />
      {isUnclaimedListConfirmationModalVisible && <UnclaimedListConfirmationModal />}
      {isRefetchClaimedListConfirmationModalVisible ? (
        <RefetchClaimedListConfirmationModal
          headerIcon={<ClipIcon boxSize="80px" />}
          title={intl.formatMessage({
            id: "flows.inbound.components.product-already-inbounded-modal.title",
          })}
          body={
            <BodyM textAlign="center">
              <FormattedMessage id="flows.inbound.components.product-already-inbounded-modal.body" />
            </BodyM>
          }
          ctaLabel={intl.formatMessage({ id: "generic.actions.refresh-page" })}
        />
      ) : null}
      {isClaimedListNotFoundWarningModalVisible && <ClaimedlistNotFoundWarningModal />}
      {isNewProductsModalVisible && <NewProductsModalModal />}
      {isProductAlreadyInbounded && <ProductAlreadyInboundedModal onClickCta={handleClose} />}
      {isScanningFailedModalVisible && <ScanningFailedModal isOpen onClickConfirm={handleClose} />}
      {isProductNotInDroppingList && (
        <AddToDroppingListConfirmationModal
          onClickYes={handleAddProductToDroppingList}
          onClickNo={handleClose}
        />
      )}
    </Page>
  );
}
